import type { ReactNode } from "react";
import type { AppContextOption, AppIdentity } from "@/app/types";
import { BottomNav, type AppTab } from "@/shared/components/BottomNav";
import { UserHeader } from "@/shared/components/UserHeader";

export type AppSurfaceMode = "root" | "detail" | "immersive" | "overlay";

export interface AppShellProps {
  children: ReactNode;
  mode?: AppSurfaceMode;
  className?: string;
  identity: AppIdentity;
  context: AppContextOption;
  capabilities?: string[];
  activeTab: AppTab;
  unreadNotifications?: number;
  contextOpen?: boolean;
  keepBottomNavigation?: boolean;
  onContextClick: () => void;
  onNotificationsClick: () => void;
  onNavigate: (tab: AppTab) => void;
}

/**
 * App Foundation 2.0.
 * A tela declara o modo de superfície; o shell decide chrome, safe-area e navegação.
 * Regras de negócio continuam nas features e capabilities do contexto ativo.
 */
export function AppShell({
  children,
  mode = "root",
  className,
  identity,
  context,
  capabilities = context.capabilities,
  activeTab,
  unreadNotifications = 0,
  contextOpen = false,
  keepBottomNavigation = false,
  onContextClick,
  onNotificationsClick,
  onNavigate,
}: AppShellProps) {
  const showRootChrome = mode === "root";
  const showBottomNav = mode === "root" && !contextOpen || mode === "detail" && keepBottomNavigation && !contextOpen;
  const shellClassName = `${className ?? ""} ${showBottomNav ? "app-shell-with-bottom-nav" : "app-shell-without-bottom-nav"}`;

  return <div
    className={`app-lifestyle min-h-screen text-white ${shellClassName}`}
    data-app-surface={mode}
    data-app-experience={context.experience}
  >
    <div className="wave-field" aria-hidden="true">{Array.from({ length: 7 }).map((_, index) => <span key={index}/>)}</div>
    {showRootChrome && <UserHeader
      identity={identity}
      context={context}
      onContextClick={onContextClick}
      onNotificationsClick={onNotificationsClick}
      unreadNotifications={unreadNotifications}
    />}
    <div className="app-shell-viewport">{children}</div>
    {showBottomNav && <BottomNav
      experience={context.experience}
      capabilities={capabilities}
      active={activeTab}
      onNavigate={onNavigate}
    />}
  </div>;
}
